<template>
  <div>
    <div v-if="endpoint.indexOf('/clients/') !== -1 || endpoint.indexOf('/users/') !== -1" class="d-flex justify-content-between mb-3">
      <div>
        <div v-if="subjects.length > 1">
          <span class="text-primary font-weight-bolder text-uppercase small">Filter: </span><b-form-checkbox-group
            id="subjects"
            name="subjects"
            v-model="selectedSubjects"
            :options="subjects"
            buttons
            class="d-inline-block"
          ></b-form-checkbox-group>
        </div>
      </div>
      <div style="min-width: 150px;" class="text-right">
        <label for="fullWebsiteLog" role="button">Show full audit log (forms/tooltips/etc)</label>
        <label class="switcher switcher-sm switcher-success ml-1 mr-0">
          <input type="checkbox" id="fullWebsiteLog" class="switcher-input" v-model="fullWebsiteLog" />
          <span class="switcher-indicator"><span class="switcher-yes"></span><span class="switcher-no"></span></span>
        </label>
      </div>
    </div>

    <div v-if="loading" class="small text-secondary text-center mb-3">
      Loading...
    </div>
    <div v-else-if="!logs.length" class="text-secondary text-center mb-3">
      No audit logs found.
      <div class="small mt-1">Everything sound-and-safe <span class="material-symbols-outlined" style="font-size: 14px; vertical-align: top;">encrypted</span></div>
    </div>

    <div
      v-for="(log, logIndex) in filteredLogs" :key="log.updated_at"
      class="card card-body"
      :class="{ 'mb-3': logIndex !== (logs.length - 1)}"
    >
      <!-- Header -->
      <div class="mb-3">
        <span class="material-symbols-outlined text-primary filled" style="vertical-align: sub;">user_attributes</span>
        <span v-b-tooltip :title="log.updated_at | datetime">{{ log.updated_at | dateRelative }}</span>
        by <a :href="`mailto:${log.user.email}`">{{ log.user.name }}</a>
        <template v-if="log.client_hash">
          for <a :href="`/administration/websites/${log.client_hash}`">{{ $root.getClient(log.client_hash)?.name ?? log.client_hash }}</a>
        </template>
      </div>

      <!-- Changes -->
      <div
        v-for="(change, changeIndex) in log.changes" :key="change.id"
        class="card card-body d-block"
        :class="{ 'mb-3': changeIndex !== (log.changes.length - 1)}"
      >
        <div class="d-flex justify-content-between">
          <div
            class="font-weight-bold text-uppercase mb-1"
            :class="{ 'text-danger': change.action === 'remove', 'text-warning': change.action === 'modify', 'text-primary': !['remove', 'modify'].includes(change.action) }"
          >
            {{ displayTitle(change.title) }}
          </div>
          <span class="text-monospace ordinalPosition unselectable text-tiny" style="position: relative; right: unset; height: fit-content;">#{{ change.id }}</span>
        </div>
        <ul class="m-0 pl-4">
          <li v-for="(c, fieldIndex) in changeToTextArray(change)" :key="`${change.id}_${fieldIndex}`">
            <template v-if="change.action === 'modify'">
              <span class="font-weight-bold">{{ c.key }}</span>:
              <template v-if="c.key.toLowerCase() === 'template'">
                <code-diff
                  language="html"
                  maxHeight="300px"
                  context="2"
                  filename="old"
                  newFilename="new"
                  :old-string="c.from"
                  :new-string="c.to"
                  output-format="side-by-side"
                />
              </template>
              <template v-else>
                <template v-if="c.key.toLowerCase() === 'status'">
                  <badge-component v-model="c.from" class="small inline" style="vertical-align: text-bottom;" />
                  <span class="small font-weight-bold"><span class="material-icons md-14 arrow-east">east</span></span>
                </template>
                <template v-else-if="isHexColor(c.from)">
                  <div :style="`display: inline-block; width: 18px; height: 18px; vertical-align: text-top; border-radius: 2px; background-color: ${c.from}`"></div><code class="inline" v-if="c.from">{{ c.from }}</code><code class="inline text-secondary" v-else>not set</code>
                  <span class="small font-weight-bold"><span class="material-icons md-14 arrow-east">east</span></span>
                </template>
                <template v-else-if="c.from !== c.to || c.from === 'redacted'">
                  <code class="inline" v-if="c.from">{{ c.from }}</code><code class="inline text-secondary" v-else>not set</code>
                  <span class="small font-weight-bold"><span class="material-icons md-14 arrow-east">east</span></span>
                </template>
                <template v-if="c.key.toLowerCase() === 'status'">
                  <badge-component v-model="c.to" class="small inline" style="vertical-align: text-bottom;" />
                </template>
                <template v-else-if="isHexColor(c.to)">
                  <div :style="`display: inline-block; width: 18px; height: 18px; vertical-align: text-top; border-radius: 2px; background-color: ${c.to}`"></div><code class="inline" v-if="c.to">{{ c.to }}</code><code class="inline text-secondary" v-else>not set</code>
                </template>
                <template v-else>
                  <code class="inline" v-if="c.to">{{ c.to }}</code><code class="inline text-secondary" v-else>not set</code>
                </template>
              </template>
            </template>
            <template v-else>
              <span class="font-weight-bold">{{ c.key }}</span>:
              <template v-if="c.key.toLowerCase() === 'template'">
                <code-diff
                  language="html"
                  maxHeight="300px"
                  :new-string="c.to"
                />
              </template>
              <template v-else>
                <template v-if="c.key.toLowerCase() === 'status'">
                  <badge-component v-model="c.to" class="small inline" style="vertical-align: text-bottom;" />
                </template>
                <template v-else-if="isHexColor(c.to)">
                  <div :style="`display: inline-block; width: 18px; height: 18px; vertical-align: text-top; border-radius: 2px; background-color: ${c.to}`"></div><code class="inline">{{ c.to }}</code>
                </template>
                <template v-else>
                  <code class="inline">{{ c.to }}</code>
                </template>
              </template>
            </template>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import BadgeComponent from '@/components/BadgeComponent.vue'

export default {
  name: 'AuditLog',
  components: { BadgeComponent },
  props: {
    endpoint: { type: String, required: true },
    labelReplacement: { type: String, default: null }
  },
  data() {
    return {
      loading: true,
      logs: null,
      fullWebsiteLog: false,
      subjectFilter: null,
    }
  },
  computed: {
    finalEndpoint () {
      let url = new URL('https://foo.bar' + this.endpoint)
      if (this.fullWebsiteLog) {
        url.searchParams.set('full', 'true')
      } else {
        url.searchParams.delete('full')
      }
      // eslint-disable-next-line vue/no-mutating-props
      return url.toString().replace('https://foo.bar', '')
    },
    subjects () {
      if (!this.logs?.length) return []

      const subjects = new Set()
      for (const l of this.logs) {
        for (const c of l.changes) {
          subjects.add(c.subject)
        }
      }

      return Array.from(subjects).sort((a, b) => a > b)
    },
    selectedSubjects: {
      get () {
        if (!this.subjectFilter) return this.subjects
        return this.subjectFilter
      },
      set (value) {
        this.subjectFilter = value
      }
    },
    filteredLogs () {
      if (!this.subjectFilter?.length) return this.logs

      const fLog = structuredClone(this.logs).map((log) => {
        const c = log.changes.filter((change) => {
          return this.subjectFilter.includes(change.subject)
        })
        if (c.length) {
          log.changes = c
          return log
        }
      }).filter(Boolean)
      return fLog
    }
  },
  methods: {
    displayTitle (title) {
      if (!title || !this.labelReplacement) return title
      return title.replace(/smart tooltip/ig, this.labelReplacement)
    },
    keyMapper (key) {
      const newKey = key.replaceAll(/[_]/gi, ' ')
      return newKey.charAt(0).toUpperCase() + newKey.slice(1) // Capitalise
    },
    changeToTextArray (change) {
      if (!change || !change.fromRaw || !change.toRaw) return []

      const results = []
      if (['modify', 'add', 'clone', 'assign', 'login', 'logout'].includes(change.action)) {
        Object.entries(change.toRaw).forEach(c => {
          const key = c[0]
          const val = c[1]

          if (['modify'].includes(change.action)) {
            // Modification
            results.push({
              key: this.keyMapper(key),
              from: change.fromRaw[key],
              to: val
            })
          } else {
            // Addition / clone
            if (val === null || (typeof val === 'string' && !val.trim().length)) return
            results.push({
              key: this.keyMapper(key),
              to: val
            })
          }
        })
      } else if (['remove', 'unassign'].includes(change.action)) {
        Object.entries(change.fromRaw).forEach(c => {
          const key = c[0]
          const val = c[1]

          // Remove
          if (val === null || (typeof val === 'string' && !val.trim().length)) return
          results.push({
            key: this.keyMapper(key),
            to: val
          })
        })
      }

      return results
    },
    isHexColor (value) {
      return /^#(?:[0-9a-fA-F]{3,4}){1,2}$/.test(value)
    },
    async reload () {
      this.loading = true
      let logs = (await this.$api.get(this.finalEndpoint, this.$root.client?.client_hash)).data

      this.loading = false
      this.logs = logs
      this.subjectFilter = null
    }
  },
  watch: {
    fullWebsiteLog: {
      immediate: true,
      async handler() {
        await this.reload()
      }
    }
  }
}
</script>

<style scoped>
ul {

}
</style>
<style>
#subjects .btn {
  margin-right: 0.35rem;
  margin-top: 0.175rem;
  margin-bottom: 0.175rem;
  border: 0 solid transparent;
  border-radius: 40px !important;
  font-size: 80%;
  padding: 2px 9px 2px 9px;
  text-transform: capitalize;
}
#subjects label.active {
  background-color: var(--exatom-green) !important;
  color: var(--exatom-blue-min1) !important;
}
</style>
